/**
 * PivotForm — authoring form for the `pivot` archetype.
 *
 * Produces the spec shape the composer expects. Grains declare a pivot_mode:
 *   - dimension — dynamic columns from a tenant-scoped dimension (e.g. scrip types)
 *   - aggregate — one row per grain entity with a single measure column (e.g. vendor spend)
 *
 * Catalog-driven: grains + companions + aggregates + summary rollups come from
 * GET /catalog/grains; dimension values (dimension pivots only) from
 * GET /catalog/grains/:grain/dimension-values.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Icons } from '../../common/Icons';
import { reportCatalogService } from '../../../services/api';

const AGGREGATES = ['SUM', 'COUNT', 'AVG', 'MIN', 'MAX'];

const PivotForm = ({ spec, onChange, targetTenantId }) => {
  const [grains, setGrains] = useState([]);
  const [dimValues, setDimValues] = useState([]);
  const [loadingDims, setLoadingDims] = useState(false);
  const [err, setErr] = useState(null);

  const grain = grains.find((g) => g.grain === spec.grain) || null;
  const isAggregate = grain?.pivot_mode === 'aggregate';
  const isReconciliation = grain?.pivot_mode === 'reconciliation';

  useEffect(() => {
    (async () => {
      try {
        const res = await reportCatalogService.getGrains(targetTenantId);
        setGrains(res.data || []);
      } catch (e) {
        const msg = e.response?.data?.message;
        setErr(msg || 'Failed to load grains.');
      }
    })();
  }, [targetTenantId]);

  // Dimension values — dimension + reconciliation grains.
  useEffect(() => {
    if (!spec.grain || isAggregate) { setDimValues([]); return; }
    const g = grains.find((x) => x.grain === spec.grain);
    if (!g || !g.dimension_source) return;
    setLoadingDims(true);
    (async () => {
      try {
        const res = await reportCatalogService.getDimensionValues(targetTenantId, spec.grain);
        setDimValues(res.data || []);
      } catch (e) {
        setErr(e.response?.data?.message || 'Failed to load dimension values for this tenant.');
        setDimValues([]);
      } finally {
        setLoadingDims(false);
      }
    })();
  }, [spec.grain, grains, targetTenantId, isAggregate]);

  const patch = useCallback((p) => onChange({ ...spec, ...p }), [spec, onChange]);

  const selectGrain = (grainName) => {
    const g = grains.find((x) => x.grain === grainName);
    if (!g) return;

    if (g.pivot_mode === 'aggregate') {
      onChange({
        grain: grainName,
        measure: { aggregate: 'SUM' },
        companion_columns: (g.companions || []).map((c) => c.key),
        summary: [],
      });
      return;
    }

    if (g.pivot_mode === 'reconciliation') {
      onChange({
        grain: grainName,
        dimension: { source: g.dimension_source || '', values: [] },
        include_other: true,
        summary: (g.summary_rollups || []).map((filter) => ({
          aggregate: 'SUM',
          filter,
          label: filter.replace(/_/g, ' '),
        })),
      });
      return;
    }

    onChange({
      ...spec,
      grain: grainName,
      dimension: { source: g.dimension_source || '', values: [] },
      measure: { aggregate: 'SUM' },
      companion_columns: [],
      include_other: false,
      summary: [],
    });
  };

  const toggleDimValue = (dv) => {
    const cur = spec.dimension?.values || [];
    const exists = cur.some((v) => v.id === dv.id);
    const next = exists ? cur.filter((v) => v.id !== dv.id) : [...cur, { id: dv.id, label: dv.label }];
    patch({ dimension: { ...spec.dimension, values: next } });
  };

  const toggleCompanion = (key) => {
    const cur = spec.companion_columns || [];
    const next = cur.includes(key) ? cur.filter((c) => c !== key) : [...cur, key];
    patch({ companion_columns: next });
  };

  const toggleSummary = (rollupKey) => {
    const cur = spec.summary || [];
    const exists = cur.some((s) => s.filter === rollupKey);
    const next = exists
      ? cur.filter((s) => s.filter !== rollupKey)
      : [...cur, { aggregate: 'SUM', filter: rollupKey, label: rollupKey.replace(/_/g, ' ') }];
    patch({ summary: next });
  };

  const selectedDimIds = new Set((spec.dimension?.values || []).map((v) => v.id));
  const selectedCompanions = new Set(spec.companion_columns || []);
  const selectedSummaries = new Set((spec.summary || []).map((s) => s.filter));

  const grainLabel = (g) => {
    const base = g.label || g.grain;
    return g.description ? `${base} — ${g.description}` : base;
  };

  return (
    <div>
      {err && <div className="alert alert-warning mb-3"><Icons.AlertCircle size={18} /><span>{err}</span></div>}

      <Field label="Grain (what each row represents)">
        <select className="form-control" value={spec.grain || ''} onChange={(e) => selectGrain(e.target.value)}>
          <option value="">Select a grain…</option>
          {grains.map((g) => <option key={g.grain} value={g.grain}>{grainLabel(g)}</option>)}
        </select>
      </Field>

      {grain && (
        <>
          {isAggregate ? (
            <Field label="Measure">
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                Each row is one {grain.label || grain.grain}. {grain.measure_label || 'Total'} is computed with the aggregate below.
              </div>
              <select
                className="form-control"
                value={spec.measure?.aggregate || 'SUM'}
                onChange={(e) => patch({ measure: { ...spec.measure, aggregate: e.target.value } })}
              >
                {AGGREGATES.map((a) => <option key={a} value={a}>{a}({grain.measure_label || 'amount'})</option>)}
              </select>
            </Field>
          ) : isReconciliation ? (
            <>
              <Field label={'Scrip types (one row each) — from ' + grain.dimension_source + ', this tenant'}>
                {loadingDims ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>
                ) : dimValues.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    This tenant has no values in {grain.dimension_source}.
                  </div>
                ) : (
                  <CheckGrid>
                    {dimValues.map((dv) => (
                      <CheckItem key={dv.id} checked={selectedDimIds.has(dv.id)} onChange={() => toggleDimValue(dv)} label={dv.label} />
                    ))}
                  </CheckGrid>
                )}
              </Field>
            </>
          ) : (
            <>
              <Field label={'Dimension values (columns) — from ' + grain.dimension_source + ', this tenant'}>
                {loadingDims ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>
                ) : dimValues.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    This tenant has no values in {grain.dimension_source}.
                  </div>
                ) : (
                  <CheckGrid>
                    {dimValues.map((dv) => (
                      <CheckItem key={dv.id} checked={selectedDimIds.has(dv.id)} onChange={() => toggleDimValue(dv)} label={dv.label} />
                    ))}
                  </CheckGrid>
                )}
              </Field>

              <Field label="Measure aggregate">
                <select
                  className="form-control"
                  value={spec.measure?.aggregate || 'SUM'}
                  onChange={(e) => patch({ measure: { ...spec.measure, aggregate: e.target.value } })}
                >
                  {AGGREGATES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
            </>
          )}

          {!isReconciliation && grain.companions.length > 0 && (
            <Field label="Companion columns">
              <CheckGrid>
                {grain.companions.map((c) => (
                  <CheckItem key={c.key} checked={selectedCompanions.has(c.key)} onChange={() => toggleCompanion(c.key)} label={c.label} />
                ))}
              </CheckGrid>
            </Field>
          )}

          {grain.summary_rollups.length > 0 && (
            <Field label="Summary totals">
              <CheckGrid>
                {grain.summary_rollups.map((r) => (
                  <CheckItem key={r} checked={selectedSummaries.has(r)} onChange={() => toggleSummary(r)} label={r.replace(/_/g, ' ')} />
                ))}
              </CheckGrid>
            </Field>
          )}

          {!isAggregate && (
            <Field label="">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!spec.include_other} onChange={(e) => patch({ include_other: e.target.checked })} />
                <span>
                  {isReconciliation
                    ? 'Include an "Other" row (scrip types not in the selected set)'
                    : 'Include an "Other" column (values not in the selected dimension set)'}
                </span>
              </label>
            </Field>
          )}

          {selectedSummaries.size > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '6px' }}>Summary labels</div>
              {(spec.summary || []).map((s, i) => (
                <div key={s.filter} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                  <span style={{ minWidth: '120px', fontSize: '13px' }}>{s.filter.replace(/_/g, ' ')}</span>
                  <input
                    className="form-control"
                    value={s.label}
                    onChange={(e) => {
                      const next = [...spec.summary];
                      next[i] = { ...s, label: e.target.value };
                      patch({ summary: next });
                    }}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '16px' }}>
    {label ? (
      <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>{label}</label>
    ) : null}
    {children}
  </div>
);

const CheckGrid = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px' }}>{children}</div>
);

const CheckItem = ({ checked, onChange, label }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span>{label}</span>
  </label>
);

export default PivotForm;
